'use client';

import {
  APP_LOCALE_LABELS,
  SOURCE_LOCALE,
  TARGET_APP_LOCALES,
} from '@govinity/shared-config/locales';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@govinity/ui/components/ui/select';
import { Languages } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import {
  applyLocaleOverride,
  isLocaleOverrideEnabled,
  readLocaleOverride,
  toOverrideLocale,
} from '@/lib/locale-override';

const BROWSER_DEFAULT = 'browser-default';

// The cookie only changes via a full reload, so there is nothing to subscribe to.
const subscribeToNothing = () => () => {};

const localeOptions = () => [
  { value: BROWSER_DEFAULT, label: 'Browser default' },
  { value: SOURCE_LOCALE, label: 'English' },
  ...TARGET_APP_LOCALES.map((locale) => ({ value: locale, label: APP_LOCALE_LABELS[locale] })),
];

/**
 * Development-only control for previewing any supported language, including ones the current
 * government has not enabled. Renders nothing outside local and integration.
 */
export const LocaleOverrideSwitcher = () => {
  const selected = useSyncExternalStore(
    subscribeToNothing,
    () => readLocaleOverride() ?? BROWSER_DEFAULT,
    () => BROWSER_DEFAULT
  );

  if (!isLocaleOverrideEnabled) return null;

  const handleChange = (value: string) => applyLocaleOverride(toOverrideLocale(value) ?? null);

  return (
    <div className="bg-background/95 fixed right-4 bottom-4 z-40 flex items-center gap-2 rounded-md border border-dashed p-2 shadow-lg backdrop-blur">
      <Languages className="text-muted-foreground size-4 shrink-0" aria-hidden />
      <Select value={selected} onValueChange={handleChange}>
        <SelectTrigger
          id="locale-override"
          aria-label="Preview language (development only)"
          className="h-8 w-44"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {localeOptions().map(({ value, label }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
